import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { Breadcrumb } from '@/components/layout/Breadcrumb';
import { InvoiceForm } from '@/components/finances/InvoiceForm';
import { invoicesService } from '@/services/invoices.service';
import type { Invoice } from '@/types';

function NewInvoiceContent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get('clientId');
  const duplicateFromId = searchParams.get('duplicateFrom');

  const [duplicateInvoice, setDuplicateInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(!!duplicateFromId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (duplicateFromId) {
      loadInvoiceToDuplicate();
    }
  }, [duplicateFromId]);

  const loadInvoiceToDuplicate = async () => {
    if (!duplicateFromId) return;

    try {
      setLoading(true);
      setError(null);
      const invoice = await invoicesService.getInvoice(duplicateFromId);
      setDuplicateInvoice(invoice);
    } catch (err: any) {
      console.error('Error loading invoice to duplicate:', err);
      setError(err.message || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    navigate('/finances/invoices');
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-flowky-primary"></div>
          <p className="text-gray-600 mt-4">Loading invoice data...</p>
        </div>
      </AppLayout>
    );
  }

  if (error && duplicateFromId) {
    return (
      <AppLayout>
        <div className="border border-border rounded-lg p-4 text-center py-12">
          <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">Error loading invoice</h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={() => navigate('/finances/invoices')}
            className="mt-4 px-4 py-2 bg-flowky-primary text-white rounded-lg hover:bg-flowky-primary/90"
          >
            Back to Invoices
          </button>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mb-6">
        <Breadcrumb
          items={[
            { label: 'Finances', href: '/finances' },
            { label: 'Invoices', href: '/finances/invoices' },
            { label: duplicateInvoice ? 'Duplicate Invoice' : 'New Invoice' },
          ]}
        />
        <h1 className="text-3xl font-bold text-gray-900 mt-2">
          {duplicateInvoice ? 'Duplicate Invoice' : 'Create New Invoice'}
        </h1>
        <p className="text-gray-600 mt-1">
          {duplicateInvoice
            ? `Creating a new invoice based on ${duplicateInvoice.invoiceNumber}`
            : 'Fill in the details to create a new invoice'}
        </p>
      </div>

      <InvoiceForm
        mode="create"
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        preselectedClientId={preselectedClientId || undefined}
        initialData={duplicateInvoice || undefined}
      />
    </AppLayout>
  );
}

export default function NewInvoicePage() {
  return (
    <ProtectedRoute>
      <NewInvoiceContent />
    </ProtectedRoute>
  );
}
